import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import { cn } from '@/lib/utils';
import { useServiceCapabilities } from '@/hooks/useServiceCapabilities';

interface ServiceCapabilityBadgesProps {
  helicopterCapabilityIds?: string[];
  engineCapabilityIds?: string[];
  maxShow?: number;
  variant?: 'default' | 'compact';
  className?: string;
}

const ServiceCapabilityBadges: React.FC<ServiceCapabilityBadgesProps> = ({
  helicopterCapabilityIds = [],
  engineCapabilityIds = [],
  maxShow = 4,
  variant = 'default',
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const { helicopters, engines, loading } = useServiceCapabilities(
    helicopterCapabilityIds,
    engineCapabilityIds
  );

  if (loading) {
    return (
      <div className={cn("flex gap-1 animate-pulse", className)}>
        <div className="h-5 w-16 bg-muted rounded" />
        <div className="h-5 w-16 bg-muted rounded" />
      </div>
    );
  }

  // Combine all capabilities
  const allCapabilities = [
    ...helicopters.map(h => ({
      type: 'helicopter' as const,
      id: h.id,
      label: h.model || `${h.manufacturer}`,
      fullLabel: h.manufacturer ? `${h.manufacturer} ${h.model}` : h.model,
      iconUrl: h.icon_url,
    })),
    ...engines.map(e => ({
      type: 'engine' as const,
      id: e.id,
      label: e.model,
      fullLabel: e.manufacturer ? `${e.manufacturer} ${e.model}` : e.model,
      iconUrl: null,
    })),
  ];

  if (allCapabilities.length === 0) {
    // Show counts if we have IDs but no resolved data
    if (helicopterCapabilityIds.length > 0 || engineCapabilityIds.length > 0) {
      return (
        <div className={cn("flex gap-1 flex-wrap", className)}>
          {helicopterCapabilityIds.length > 0 && (
            <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-900/20 border-blue-200/60 text-blue-700 dark:text-blue-300">
              <HelicopterSilhouetteIcon className="w-3 h-3 mr-1" />
              {helicopterCapabilityIds.length} types
            </Badge>
          )}
          {engineCapabilityIds.length > 0 && (
            <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800">
              <Settings className="w-3 h-3 mr-1" />
              {engineCapabilityIds.length} engines
            </Badge>
          )}
        </div>
      );
    }
    return null;
  }

  const visibleItems = isExpanded ? allCapabilities : allCapabilities.slice(0, maxShow);
  const hiddenCount = allCapabilities.length - maxShow;
  const showExpandButton = hiddenCount > 0;

  // Compact variant for cards
  if (variant === 'compact') {
    return (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {visibleItems.slice(0, 4).map((cap, idx) => (
          <Badge 
            key={cap.id || idx}
            variant="outline"
            className={cn(
              "text-[10px] px-1.5 py-0.5 font-medium gap-1",
              cap.type === 'helicopter' 
                ? "bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/40 dark:to-purple-900/30 border-violet-200 dark:border-violet-700/50 text-violet-700 dark:text-violet-300"
                : "bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/40 border-slate-200 dark:border-slate-600/50 text-slate-700 dark:text-slate-300"
            )}
          >
            {cap.iconUrl ? (
              <img 
                src={cap.iconUrl} 
                alt="" 
                className="w-4 h-4 object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : cap.type === 'helicopter' ? (
              <HelicopterSilhouetteIcon className="w-3 h-3" />
            ) : (
              <Settings className="w-3 h-3" />
            )}
            {cap.label.slice(0, 15)}
          </Badge>
        ))}
        {allCapabilities.length > 4 && (
          <Badge 
            variant="secondary" 
            className="text-[10px] px-1.5 py-0.5 bg-muted/80"
          >
            +{allCapabilities.length - 4}
          </Badge>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap gap-1.5">
        {visibleItems.map((cap, idx) => (
          <Badge 
            key={cap.id || idx}
            variant="outline"
            className={cn(
              "text-xs px-2 py-1 font-medium gap-1.5 shadow-sm",
              cap.type === 'helicopter' 
                ? "bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/40 dark:to-purple-900/30 border-violet-200 dark:border-violet-700/50 text-violet-700 dark:text-violet-300"
                : "bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/40 border-slate-200 dark:border-slate-600/50 text-slate-700 dark:text-slate-300"
            )}
          >
            {cap.iconUrl ? (
              <img 
                src={cap.iconUrl} 
                alt="" 
                className="w-5 h-5 object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : cap.type === 'helicopter' ? (
              <HelicopterSilhouetteIcon className="w-3.5 h-3.5" />
            ) : (
              <Settings className="w-3.5 h-3.5" />
            )}
            {cap.fullLabel}
          </Badge>
        ))}
      </div>

      {showExpandButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3 mr-1" />
              +{hiddenCount} more capabilities
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default ServiceCapabilityBadges;
